/** @typedef {import('../../api/walletConversions').ConversionQuote} ConversionQuote */
/** @typedef {import('../../api/walletConversions').WalletConversion} WalletConversion */
/** @typedef {import('../../types/api').ApiResponse} ApiResponse */

import { useMutation, useQueryClient } from 'react-query';

import {
  createConversionQuote as createConversionQuoteAction,
  createWalletConversion as createWalletConversionAction
} from '@basqet-dashboard/common/api';

/**
 * Generate a conversion quote. Quotes are short-lived; the response carries
 * an `expires_at` ISO timestamp the UI tracks for the countdown.
 *
 * @returns {import('react-query').UseMutationResult<
 *   ApiResponse<ConversionQuote>, unknown,
 *   { from_currency: string, to_currency: string, amount: number }>}
 */
export const useCreateConversionQuoteMutation = () =>
  useMutation({
    mutationFn: (payload) => createConversionQuoteAction(payload)
  });

/**
 * Confirm a previously-generated quote and execute the conversion. On
 * success the list cache is invalidated so the new row shows up on
 * navigation back to the conversions list.
 *
 * @returns {import('react-query').UseMutationResult<
 *   ApiResponse<WalletConversion>, unknown,
 *   { quote_id: string, reference?: string }>}
 */
export const useCreateWalletConversionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => createWalletConversionAction(payload),
    onSuccess: () => {
      queryClient.invalidateQueries(['walletConversions'], {
        refetchActive: true,
        refetchInactive: true
      });
    }
  });
};
